/**
 * One label point per polygon feature, for GeoJSON layers (staff-uploaded
 * district maps). MapLibre tiles GeoJSON internally, so labeling polygons
 * directly repeats a name on every tile-cut piece.
 *
 * The point is inside the feature's largest part: its centroid when that
 * falls inside, otherwise the middle of the widest horizontal run through
 * the shape at the centroid's latitude (so concave and L-shaped districts
 * still get an interior label).
 */
import type { Feature, FeatureCollection, Point, Position } from 'geojson';

type Ring = Position[];
type PolygonCoords = Ring[];

function ringArea(ring: Ring): number {
  let sum = 0;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const [xi = 0, yi = 0] = ring[i] ?? [];
    const [xj = 0, yj = 0] = ring[j] ?? [];
    sum += (xj - xi) * (yj + yi);
  }
  return Math.abs(sum / 2);
}

function ringCentroid(ring: Ring): [number, number] {
  let a = 0;
  let cx = 0;
  let cy = 0;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const [xi = 0, yi = 0] = ring[i] ?? [];
    const [xj = 0, yj = 0] = ring[j] ?? [];
    const f = xj * yi - xi * yj;
    a += f;
    cx += (xj + xi) * f;
    cy += (yj + yi) * f;
  }
  if (Math.abs(a) < 1e-15) {
    const [x = 0, y = 0] = ring[0] ?? [];
    return [x, y];
  }
  return [cx / (3 * a), cy / (3 * a)];
}

function inRing(x: number, y: number, ring: Ring): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const [xi = 0, yi = 0] = ring[i] ?? [];
    const [xj = 0, yj = 0] = ring[j] ?? [];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

function inPolygon(x: number, y: number, poly: PolygonCoords): boolean {
  const [outer, ...holes] = poly;
  if (!outer || !inRing(x, y, outer)) return false;
  return !holes.some((hole) => inRing(x, y, hole));
}

/** Middle of the widest interior run along the horizontal line at `y`. */
function scanlinePoint(poly: PolygonCoords, y: number): [number, number] | null {
  const xs: number[] = [];
  for (const ring of poly) {
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
      const [xi = 0, yi = 0] = ring[i] ?? [];
      const [xj = 0, yj = 0] = ring[j] ?? [];
      if (yi > y !== yj > y) xs.push(((xj - xi) * (y - yi)) / (yj - yi) + xi);
    }
  }
  xs.sort((a, b) => a - b);
  let best: [number, number] | null = null;
  let width = -1;
  for (let k = 0; k + 1 < xs.length; k += 2) {
    const a = xs[k] ?? 0;
    const b = xs[k + 1] ?? 0;
    if (b - a > width) {
      width = b - a;
      best = [(a + b) / 2, y];
    }
  }
  return best;
}

export function polygonLabelPoint(poly: PolygonCoords): [number, number] | null {
  const outer = poly[0];
  if (!outer || outer.length < 4) return null;
  const [cx, cy] = ringCentroid(outer);
  if (inPolygon(cx, cy, poly)) return [cx, cy];
  return scanlinePoint(poly, cy);
}

export function labelPoints(fc: FeatureCollection): FeatureCollection<Point> {
  const features: Feature<Point>[] = [];
  for (const feature of fc.features) {
    const g = feature.geometry;
    let polys: PolygonCoords[] = [];
    if (g?.type === 'Polygon') polys = [g.coordinates];
    else if (g?.type === 'MultiPolygon') polys = g.coordinates;
    if (!polys.length) continue;
    const largest = polys.reduce((best, p) => (ringArea(p[0] ?? []) > ringArea(best[0] ?? []) ? p : best));
    const point = polygonLabelPoint(largest);
    if (!point) continue;
    features.push({
      type: 'Feature',
      properties: feature.properties ?? {},
      geometry: { type: 'Point', coordinates: point },
    });
  }
  return { type: 'FeatureCollection', features };
}
